import h from 'hyperscript';
import warningIcon from '@disy/cadenza-icons/warning.svg';
import grayMarker from '@disy/cadenza-icons/pointer-gray.svg';
import blueMarker from '@disy/cadenza-icons/pointer-blue.svg';
import clearIcon from '@disy/cadenza-icons/clear-round.svg';
import searchActiveIcon from '@disy/cadenza-icons/search-active.svg';
import searchIcon from '@disy/cadenza-icons/search.svg';
import arrowRight from '@disy/cadenza-icons/arrow-right.svg';
import { unByKey } from 'ol/Observable';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Style from 'ol/style/Style';
import 'jquery-ui/ui/widgets/autocomplete';
import Icon from 'ol/style/Icon';
import VectorEventType from 'ol/source/VectorEventType';
import groupBy from 'lodash-es/groupBy';

import type { AutocompleteItem } from 'ui/autocomplete-input/autocomplete-input';
import {
  AutocompleteInput,
  ClosedEvent,
  FocussedEvent,
  SelectedEvent
} from 'ui/autocomplete-input/autocomplete-input';
import type { ProgressSpinner } from 'ui/progress-spinner/progress-spinner';
import { createToggletip } from 'ui/toggletip/toggletip';

import { MapProperty } from 'cadenza/map/map-property';
import { logger } from 'cadenza/utils/logging';
import { fitMapViewToScaledExtent } from 'cadenza/map/map-utils';
import { icon as createIcon, icon } from 'cadenza/utils/icon/icon';
import { clearAndRefreshVectorLayer } from 'cadenza/map/layer-utils';
import { mediaSmUp } from 'cadenza/utils/media';
import { AbortError } from 'cadenza/api-client/abort-controller/abort-error';
import { MapPopupHandler } from 'cadenza/map/map-popup-handler';
import { confirm } from 'cadenza/alert';
import { LocationFinderMobileModal } from 'cadenza/map/location-finder/location-finder-mobile-modal';
import { LocationFinderInteraction } from 'cadenza/map/location-finder/location-finder-interaction';
import { LocationFinderInfoPanel } from 'cadenza/map/location-finder/location-finder-info-panel';
import type { LocationFinderSearchConfiguration } from 'cadenza/map/location-finder/location-finder-utils';
import {
  createLocationFinderDropdown,
  isLocationFinderSearchRestricted
} from 'cadenza/map/location-finder/location-finder-utils';
// underscore because of name conflict with built-in TypeScript type "Map"
import type { Map as CadenzaMap } from 'cadenza/map/map';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import type { LocationFinderEvent } from 'cadenza/map/location-finder/location-finder-event';
import { assertNonNullable } from 'cadenza/utils/custom-error';
import { on } from 'cadenza/utils/event-util';
import type {
  DataProviderId,
  DataProviderType,
  GeocodedAddressDto,
  LocationFinderSettingsDto
} from 'cadenza/admin/system-settings/location-finder-section/location-finder-admin-page-api-client';
import {
  dataProviderIdAsString
} from 'cadenza/admin/system-settings/location-finder-section/location-finder-admin-page-api-client';
import type {
  LocationFinderDataProviderDto,
  SuggestionDto,
  SuggestionsDto
} from 'cadenza/map/location-finder/location-finder-api-client';
import { getLocationFinderDataProviders, getSuggestions } from 'cadenza/map/location-finder/location-finder-api-client';

import type { EventsKey } from 'ol/events';
import type Layer from 'ol/layer/Layer';
import type { Geometry } from 'ol/geom';
import type { Coordinate } from 'ol/coordinate';

import i18n from './location-finder.properties';
import './location-finder.css';

const COMPONENT_NAME = 'd-location-finder';

const POPUP_STYLE = 'd-location-finder--info-popup';
const SEARCH_FIELD_STYLE = 'd-location-finder--search-field';

export const ITEM_SELECTED_EVENT = 'itemSelected';
export const ITEM_SELECTED_REMOVED_EVENT = 'itemSelectedRemoved';

export interface LocationFinderOptions {
  /**
   * if true, when a Location Finder result is selected, instead of zooming the map to the marker
   * for the selected item, the Location Finder markers on the map are hidden.
   */
  hideMarkersAfterSelect?: boolean;
  /** if false the button and dropdown menu to select providers are not included in the component */
  enableDropdownMenu?: boolean;
  /**
   * if true the label with the selected item label will be included in the mobile view
   * (not desired in the toolbar one)
   */
  showMobileLabel?: boolean;
  disabled?: boolean;
  /**
   * Mutable searchConfig for RoutingLocationFinderFactory.
   */
  searchConfig?: LocationFinderSearchConfiguration;
}

export interface LocationFinderAutocompleteItemData {
  providerId: DataProviderId;
  dataProviderType: DataProviderType;
  bbox: WorldBoxDto;
  inputFieldSearchString: string;
  outOfAreaOfUse: boolean;
  outOfMapAreaOfUse: boolean;
  address?: GeocodedAddressDto;
  formattedAddress?: string;
  attributes?: Map<string, string>;
  maxZoomScale?: number;
  marker: [number, number];
  srsCode: string;
}

export class LocationFinder extends HTMLElement {

  readonly #map;
  readonly #hideMarkersAfterSelect;
  readonly #enableDropdownMenu;
  readonly #showMobileLabel;
  readonly #mapPopupHandler;

  readonly #components;
  readonly #searchField: AutocompleteInput<LocationFinderAutocompleteItemData>;
  readonly #progressSpinner;
  readonly #clearButton;
  readonly #backButton;
  readonly #providersButton?: HTMLButtonElement;

  #lastSelectedItem?: AutocompleteItem<LocationFinderAutocompleteItemData>;
  #abortController?: AbortController;

  #disabledFirstMoveEvent;
  readonly #highlightStyle;
  readonly #vectorSource;
  readonly #vectorLayer;
  readonly #interaction;
  #highlightedFeature: Feature<Geometry> | null;

  #providersDropdownAppendElement?: HTMLElement;
  #providersDropdownWithHeading?: boolean;
  #mobileLabel?: HTMLElement;

  // mutable search configuration, initialized from database configuration, modifiable in LocationFinderProvidersDropdown
  #searchConfig: LocationFinderSearchConfiguration;

  #eventKeys: EventsKey[] = [];

  /**
   * Constructor
   *
   * @param map - the map where the Location Finder results are to be marked
   * @param settingsConfig - required configuration for the Location Finder
   * @param [configuration] - optional configuration
   */
  constructor (map: CadenzaMap, settingsConfig: LocationFinderSettingsDto, {
    hideMarkersAfterSelect = false,
    enableDropdownMenu = true,
    showMobileLabel = true,
    disabled = false,
    searchConfig = undefined
  }: LocationFinderOptions = {}) {
    /*
     * Once LocationFinder is shown only as a button in the map toolbar, we should refactor this and
     * remove config from constructor parameters. Config can then be queried inside this component.
     * We don't do that now because we need config to create the search input field (which is currently in the toolbar).
     * Querying for the configuration in the connectedCallback would cause "jumpy" toolbar for users,
     * because of adding search input.
     */
    super();
    this.#map = map;
    this.#hideMarkersAfterSelect = hideMarkersAfterSelect;
    this.#enableDropdownMenu = enableDropdownMenu;
    this.#showMobileLabel = showMobileLabel;
    this.#mapPopupHandler = new MapPopupHandler(this.#map);

    this.classList.add(COMPONENT_NAME);

    this.#searchConfig = searchConfig || {
      useCurrentBBox: settingsConfig.useCurrentBBox,
      dataProviders: []
    };

    const { placeholderText = i18n('searchTextPlaceholder') } = settingsConfig;
    const input = h<HTMLInputElement>(
      `input.${SEARCH_FIELD_STYLE}`,
      {
        placeholder: placeholderText,
        title: placeholderText,
        disabled: this.disabled
      });
    this.#searchField = this.#createAutocompleteComponent(input);
    this.disabled = disabled;

    this.#progressSpinner = h<ProgressSpinner>(
      'd-progress-spinner.d-location-finder--suggestions-progress-spinner',
      { hidden: true });
    this.#clearButton = h('button.button.button-icon.button-s.button-borderless.d-location-finder--clear-button', {
      type: 'button',
      title: i18n('clearButton.title'),
      onclick: () => {
        this.#clearInputFieldAndSuggestions();
      },
      hidden: true
    }, icon(clearIcon));
    this.#backButton = h('button.button.button-icon.button-borderless.d-location-finder--back-button', {
      type: 'button',
      title: i18n('backButton.title'),
      onclick: this.#onClickBackButton.bind(this),
      hidden: true
    }, icon(arrowRight));

    this.#components = [ this.#searchField, this.#progressSpinner, this.#clearButton, this.#backButton ];

    if (this.#enableDropdownMenu) {
      this.#providersButton = h<HTMLButtonElement>('button.button.button-icon.button-borderless.button-with-dropdown.d-location-finder--providers-button', {
        type: 'button',
        title: placeholderText,
        onclick: this.#onClickProvidersButton.bind(this),
        attrs: { 'aria-expanded': 'false' },
        disabled
      }, icon(searchIcon));
      this.#components.unshift(this.#providersButton);
    }

    this.#onMediaSmUpChanged(mediaSmUp);

    // helper flag for handling map moveend events
    this.#disabledFirstMoveEvent = false;

    const defaultStyle = createIconStyle(grayMarker, [ 24, 24 ]);
    this.#highlightStyle = createIconStyle(blueMarker, [ 36, 36 ]);

    this.#vectorSource = new VectorSource();
    this.#vectorLayer = new VectorLayer({
      source: this.#vectorSource,
      map: this.#map,
      style: defaultStyle
    });

    this.#interaction = new LocationFinderInteraction(
      this.#map,
      { layerFilter: (layer: Layer) => layer === this.#vectorLayer }
    );
    this.#interaction.setActive(false);
    this.#map.addInteraction(this.#interaction);

    this.#highlightedFeature = null;
  }

  async connectedCallback () {
    // Detect if waypoint was created without constructor parameters
    // (as ghost created by JQuery clone method on drag&drop with SortableJS).
    if (!this.#map) {
      return;
    }

    this.#searchConfig.dataProviders = await getLocationFinderDataProviders();

    // events handlers
    this.#eventKeys = [
      this.#map.on('moveend', this.#onMovedMap.bind(this)),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore on() allows arbitrary event types but its type definition does not reflect that
      this.#map.on(`change:${MapProperty.PRIMARY_INTERACTION}`, () => this.#makeSureLocationFinderInteractionIsLast()),

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore on() allows arbitrary event types but its type definition does not reflect that
      ...this.#vectorSource.on([ VectorEventType.ADDFEATURE, VectorEventType.REMOVEFEATURE, VectorEventType.CLEAR ], () => this.#toggleInteractionActivity()),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore on() allows arbitrary event types but its type definition does not reflect that
      this.#interaction.on('locationFinder.hover', (e: LocationFinderEvent) => this.#handleHoverEvent(e)),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore on() allows arbitrary event types but its type definition does not reflect that
      this.#interaction.on('locationFinder.click', (e: LocationFinderEvent) => this.#handleClickEvent(e)),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore on() allows arbitrary event types but its type definition does not reflect that
      this.#interaction.on('locationFinder.clearFlyoutHandler', _ => this.#mapPopupHandler.close())
    ];

    mediaSmUp.addEventListener('change', e => this.#onMediaSmUpChanged(e));
  }

  #onMediaSmUpChanged ({ matches }: MediaQueryList | MediaQueryListEvent) {
    this.innerHTML = '';
    if (matches) {
      this.append(...this.#components);
      this.#providersDropdownAppendElement = document.body;
      this.#providersDropdownWithHeading = true;
    } else {
      this.#providersDropdownWithHeading = false;
      this.append(h('button.button.button-icon.button-borderless', {
        type: 'button',
        title: i18n('locationFinderMobileButton.title'),
        onclick: () => {
          const modal = new LocationFinderMobileModal(this.#components, this.#clearInputFieldAndSuggestions.bind(this));
          modal.connectAndShow();
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore we won't need this anymore soon with the new modal UI; not worth refactoring before
          this.#providersDropdownAppendElement = modal.body;
        }
      }, icon(searchIcon)));
      this.#appendMobileLabel();
    }
  }

  disconnectedCallback () {
    unByKey(this.#eventKeys);
    mediaSmUp.removeEventListener('change', this.#onMediaSmUpChanged);
  }

  #appendMobileLabel () {
    if (this.#showMobileLabel) {
      this.#mobileLabel = h('span.mobile-label');
      this.append(this.#mobileLabel);
    }
  }

  #onClickProvidersButton () {
    // if popup was open don't reopen it again
    if (this.#providersButton!.getAttribute('aria-expanded') === 'true') {
      return;
    }
    createLocationFinderDropdown(
      this.#providersButton!,
      this.#searchField,
      this.#searchConfig,
      {
        onChange: (configuration: LocationFinderSearchConfiguration) => this.#updateWithConfiguration(configuration),
        onClose: (configuration: LocationFinderSearchConfiguration) => {
          this.#searchConfig = configuration;
        },
        showHeader: this.#providersDropdownWithHeading
      }
    );
  }

  #updateWithConfiguration (configuration: LocationFinderSearchConfiguration) {
    if (!this.#providersButton) {
      return;
    }
    this.#searchConfig = configuration;
    const filtersActive = isLocationFinderSearchRestricted(this.#searchConfig);
    this.#providersButton.classList.toggle('search-restricted', filtersActive);
    this.#providersButton.querySelector('svg')!.replaceWith(
      filtersActive ? icon(searchActiveIcon) : icon(searchIcon));
  }

  #getMapSrs () {
    return this.#map.getView().getProjection().getCode();
  }

  #getMapExtent () {
    return this.#map.getView().calculateVisibleExtent();
  }

  get selectedItem (): AutocompleteItem<LocationFinderAutocompleteItemData> | undefined {
    return this.#lastSelectedItem;
  }

  set selectedItem (item: AutocompleteItem<LocationFinderAutocompleteItemData> | null) {
    if (item) {
      if (!item.data.outOfMapAreaOfUse) {
        this.#selectItem(item);
      } else {
        const message = i18n('outOfMapAreaOfUse.message');
        confirm('', message, { type: 'warning' })
          .then(confirmed => {
            if (confirmed) {
              this.#selectItem(item);
            } else {
              this.#clearInputFieldAndSuggestions();
            }
          })
          .catch(error => logger.error('Error selecting item', error));
      }
    } else {
      this.#lastSelectedItem = undefined;
    }

    this.#clearButton.hidden = !this.#searchField.value;
    this.#backButton.hidden = true;
  }

  get selectedBbox () {
    return this.selectedItem?.data.bbox;
  }

  get searchText () {
    return this.#searchField.value;
  }

  set searchText (text) {
    this.#updateSelectedItemLabel(text);
    this.#clearButton.hidden = !this.#searchField.value;
  }

  setSearchTextAndTriggerAutocomplete (text: string) {
    this.#searchField.value = text;
    this.#searchField.refreshSuggestions();
  }

  #selectItem (item: AutocompleteItem<LocationFinderAutocompleteItemData>) {
    this.#updateSelectedItemLabel(item.label);
    this.#lastSelectedItem = item;
    const feature = this.#vectorSource.getFeatureById(item.id) as (Feature<Point> | null);
    clearAndRefreshVectorLayer(this.#vectorLayer);
    if (feature) {
      this.#vectorSource.addFeature(feature);
    }

    if (!this.#searchConfig.useCurrentBBox && !this.#hideMarkersAfterSelect) {
      this.#displayBoundingBoxOnMap(item.data.bbox, item.data.maxZoomScale)
        .then(_ => {
          // has to happen after the zoom, because on zoom the flyout is closed automatically
          if (feature && item.data.attributes) {
            this.#showFeatureInfoFlyout(item, feature);
          }
        })
        .catch(() => logger.error('Failed to show location finder result info'));
    }

    if (this.#hideMarkersAfterSelect) {
      this.#vectorSource.clear();
    }

    this.#raiseItemSelectedEvent(item);
  }

  #showFeatureInfoFlyout (item: AutocompleteItem<LocationFinderAutocompleteItemData>, feature: Feature<Point>) {
    setTimeout(() => {
      // The promise returned by _displayBoundingBoxOnMap resolves a bit too early, when the animation
      // is not completely finished yet. The setTimeout here is needed for the flyout not to be misplaced.
      assertNonNullable(item.data.attributes);
      this.#showInfoFlyout(
        // We add a geometry to every feature in #displaySuggestionsOnMap, so getGeometry
        // cannot return undefined.
        feature.getGeometry()!.getCoordinates(),
        item.data.attributes,
        createAddressPopupContent(item.data));
    }, 100);
  }

  #updateSelectedItemLabel (label?: string) {
    this.#updateSearchField(label);
    this.#updateMobileLabel(label);
  }

  #updateSearchField (label?: string) {
    this.#searchField.value = label ?? '';
  }

  #updateMobileLabel (label?: string) {
    if (this.#mobileLabel) {
      this.#mobileLabel.textContent = label ?? '';
    }
  }

  #raiseItemSelectedEvent (item: AutocompleteItem<LocationFinderAutocompleteItemData>) {
    this.dispatchEvent(new CustomEvent(
      ITEM_SELECTED_EVENT,
      {
        detail: {
          inputFieldSearchString: item.data.inputFieldSearchString,
          bbox: item.data.bbox
        }
      }));
  }

  #showInfoFlyout (coordinates: Coordinate, attributes: Map<string, string>, addressContent?: string | Node) {
    this.#mapPopupHandler.displayPopup(coordinates, new LocationFinderInfoPanel(addressContent, attributes), POPUP_STYLE);
  }

  #makeSureLocationFinderInteractionIsLast () {
    if (!this.#interaction.getActive()) {
      return;
    }
    const interactions = this.#map.getInteractions();
    const InteractionIndex = interactions.getArray().findIndex(i => i instanceof LocationFinderInteraction);
    if (InteractionIndex !== interactions.getLength() - 1) {
      interactions.remove(this.#interaction);
      interactions.push(this.#interaction);
    }
  }

  #toggleInteractionActivity () {
    const active = this.#vectorSource.getFeatures().length > 0;
    this.#interaction.setActive(active);
    this.#makeSureLocationFinderInteractionIsLast();
  }

  #onMovedMap () {
    if (this.#disabledFirstMoveEvent) {
      this.#disabledFirstMoveEvent = false;
    } else if (this.selectedItem && !this.#hideMarkersAfterSelect) {
      this.#backButton.hidden = false;
    }
  }

  #displayBoundingBoxOnMap (boundingBox: WorldBoxDto, maxZoomScale?: number) {
    this.#disabledFirstMoveEvent = true;
    // Add 20% to the bounding box, so no points are displayed close to the map view border.
    return fitMapViewToScaledExtent(this.#map, boundingBox, { maxZoomScale });
  }

  #onClickBackButton () {
    if (this.selectedItem) {
      this.#displayBoundingBoxOnMap(this.selectedItem.data.bbox, this.selectedItem.data.maxZoomScale);
    }
    this.#backButton.hidden = true;
  }

  #clearInputFieldAndSuggestions () {
    this.#updateSelectedItemLabel();
    this.selectedItem = null;
    if (this.#abortController) {
      this.#abortController.abort();
    }
    clearAndRefreshVectorLayer(this.#vectorLayer);
    this.#raiseItemSelectedRemovedEvent();
  }

  #raiseItemSelectedRemovedEvent () {
    this.dispatchEvent(new CustomEvent(ITEM_SELECTED_REMOVED_EVENT));
  }

  /**
   * Transforms suggestions data received from REST API to format accepted by autocomplete component.
   *
   * @param suggestionsDto - data received by REST API
   * @return array of items for the autocomplete component, where item is in format {id: ..., label: ..., category: ..., bbox: ...}
   * @private
   */
  #transformSuggestions (suggestionsDto: SuggestionsDto) {
    const dataProviderById = Object.fromEntries(this.#searchConfig.dataProviders.map(dataProvider => [ dataProviderIdAsString(dataProvider.id), dataProvider ]));
    return Object.entries(groupBy(suggestionsDto.suggestions, suggestion => dataProviderIdAsString(suggestion.dataProviderId)))
      .flatMap(([ dataProviderId, suggestions ]) => {
        const provider = dataProviderById[dataProviderId];
        return (provider && suggestions.map(suggestion => createAutocompleteItem(provider, suggestion))) || [];
      });
  }

  #createAutocompleteComponent (input: HTMLInputElement) {
    const autocompleteInput = new AutocompleteInput<LocationFinderAutocompleteItemData>(
      (searchText) => this.#querySuggestions(searchText)
        .then(result => this.#handleSuggestionsResult(result))
        .catch(error => this.#handleQuerySuggestionsError(error))
        .finally(() => (this.#progressSpinner.hidden = true)),
      {
        input,
        popupTarget: this,
        debounceWait: 1000
      }
    );
    on<SelectedEvent<LocationFinderAutocompleteItemData>>(
      autocompleteInput,
      SelectedEvent.type,
      e => {
        const item = e.detail.item;
        this.selectedItem = item;
        this.#updateSearchField(item.data.inputFieldSearchString);
      });
    on<FocussedEvent<LocationFinderAutocompleteItemData>>(
      autocompleteInput,
      FocussedEvent.type,
      e => {
        if (e.detail.item) {
          this.#highlight(e.detail.item.id);
        }
      });
    on(autocompleteInput, ClosedEvent.type, () => {
      if (!this.#lastSelectedItem) {
        clearAndRefreshVectorLayer(this.#vectorLayer);
      }
    });
    input.addEventListener('input', () => {
      this.selectedItem = null;
    });
    return autocompleteInput;
  }

  #querySuggestions (searchText: string) {
    this.#progressSpinner.hidden = false;
    const selectedProviders = this.#searchConfig.dataProviders
      .filter(provider=> provider.checked)
      .map(provider => provider.id);
    this.#abortController = new AbortController();
    return getSuggestions(
      this.#getMapSrs(),
      this.#getMapExtent(),
      prepareSearchText(searchText),
      this.#searchConfig.useCurrentBBox ?? false,
      selectedProviders,
      this.#abortController.signal
    );
  }

  #handleSuggestionsResult (suggestions: SuggestionsDto) {
    this.#displaySuggestionsOnMap(suggestions);
    if (!this.#searchConfig.useCurrentBBox) {
      this.#zoomOutToDisplaySuggestionsIfNeeded(suggestions);
    }
    return this.#transformSuggestions(suggestions);
  }

  #handleQuerySuggestionsError (error: Error) {
    if (!(error instanceof AbortError)) {
      logger.error('Failed to load location finder suggestions', error);
    }
    return [];
  }

  #displaySuggestionsOnMap (suggestionsDto: SuggestionsDto) {
    clearAndRefreshVectorLayer(this.#vectorLayer);
    this.#vectorSource.addFeatures(suggestionsDto.suggestions.map(suggestion => {
      const feature = new Feature({
        geometry: new Point(suggestion.marker),
        name: suggestion.label
      });
      feature.setId(dataProviderIdAsString(suggestion.dataProviderId) + '-' + suggestion.id);
      return feature;
    }));
  }

  #zoomOutToDisplaySuggestionsIfNeeded (suggestionsDataApiFormat: SuggestionsDto) {
    const bbox = suggestionsDataApiFormat.bbox;
    if (bbox) {
      const extent = this.#map.getView().calculateVisibleExtent();
      if (extent[0] > bbox[0] || extent[1] > bbox[1] || extent[2] < bbox[2] || extent[3] < bbox[3]) {
        this.#displayBoundingBoxOnMap(bbox);
      }
    }
  }

  #highlight (id: string) {
    if (this.#highlightedFeature) {
      this.#highlightedFeature.setStyle();
    }
    const feature = this.#vectorSource.getFeatureById(id);
    this.#highlightedFeature = feature as Feature | null;
    if (this.#highlightedFeature) {
      this.#highlightedFeature.setStyle(this.#highlightStyle);
    }
  }

  #handleHoverEvent (locationFinderEvent: LocationFinderEvent) {
    const feature = locationFinderEvent.feature;
    this.#searchField?.focusById(feature.getId());
  }

  #handleClickEvent (locationFinderEvent: LocationFinderEvent) {
    const feature = locationFinderEvent.feature;
    this.#searchField?.selectById(feature.getId());
    const item = this.#lastSelectedItem;
    if (item && item.data.attributes) {
      this.#showFeatureInfoFlyout(item, feature);
    }
  }

  connectSpinner () { // used in clone
    this.#progressSpinner.connectedCallback();
  }

  get disabled () {
    return this.hasAttribute('disabled');
  }

  set disabled (value) {
    this.#searchField.disabled = value;
    this.toggleAttribute('disabled', value);
  }

}

customElements.define(COMPONENT_NAME, LocationFinder);

/**
 * Prepares a search term for the Location Finder search, replaces space-like characters with a standard space character.
 *
 * Java Regex does not recognize some specific space characters like "&ensp;" with '\s', but JS does it.
 * So we replace all space-like characters with a simple space here.
 *
 * We use a character "&ensp;" in the output of the coordinates under mouse cursor and capture-coordinates interaction to make the coordinates readable,
 * with just a space they are too close together. If the user has copied the coordinates, it should be possible to use them for the Location Finder search.
 *
 * @param term - Search term
 * @return A preprocessed term, where different space-like characters are replaced with a simple space character.
 */
function prepareSearchText (term: string) {
  return term?.replace(/\s/g, ' ');
}

function createIconStyle (svgIcon: string, size: [number, number]) {
  return new Style({
    image: new Icon({
      src: 'data:image/svg+xml;base64,' + btoa(svgIcon),
      size,
      anchor: [ 0.5, 1 ]
    })
  });
}

export function createWarningToggletipForNoProvidersConfigured () {
  return createToggletip({
    toggletipContent: () => renderMessage(),
    styleClass: 'button-warning',
    icon: createIcon(warningIcon),
    size: 'm',
    title: i18n('noProviders.tooltip')
  });
}

function renderMessage () {
  return h(`${COMPONENT_NAME}--messages.d-stack-v.space-2`,
    h('d-alert.d-alert-small',
      {
        attrs: {
          type: 'warning',
          message: i18n('noProviders.message')
        }
      }
    )
  );
}

function createAutocompleteItem (dataProvider: LocationFinderDataProviderDto, suggestion: SuggestionDto) {
  // maxZoomScale is 0 if not present, which breaks zooming because it tries to zoom to point, undefined will lead to using default scale
  const maxZoomScale = dataProvider.maxZoomScale > 0 ? dataProvider.maxZoomScale : undefined;
  const {
    id,
    dataProviderType,
    listItemHeadline,
    label,
    bbox,
    inputFieldSearchString,
    outOfAreaOfUse,
    outOfMapAreaOfUse,
    address,
    formattedAddress,
    attributes,
    marker,
    srsCode
  } = suggestion;
  const category = listItemHeadline ?? dataProvider.label;
  const item: AutocompleteItem<LocationFinderAutocompleteItemData> = {
    id: dataProviderIdAsString(dataProvider.id) + '-' + id,
    label,
    category,
    data: {
      providerId: dataProvider.id,
      dataProviderType,
      bbox,
      inputFieldSearchString,
      outOfAreaOfUse,
      outOfMapAreaOfUse,
      address,
      formattedAddress,
      attributes,
      maxZoomScale,
      marker,
      srsCode
    }
  };
  return item;
}

function createAddressPopupContent (data: LocationFinderAutocompleteItemData) {
  if (data.dataProviderType === 'WHAT_3_WORDS') {
    return h('d-what3words-label', { words: data.formattedAddress });
  } else {
    return data.formattedAddress;
  }
}
